import type { QueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type AssignabilityResult =
  | { ok: true }
  | { ok: false; reason: "needs_service"; trailerId: string; serviceReason: string | null };

interface CachedTrailer {
  trailer_id: string;
  needs_service?: boolean | null;
  needs_service_reason?: string | null;
}

/**
 * Single source of truth for whether a trailer is assignable to an order.
 *
 * Reads from the shared `["checked-in-trailers", tenantId]` React Query
 * cache first (zero extra round trips for the common path where the
 * dispatcher picked from the combobox). Falls back to a one-off lookup
 * against `public.trailers` for typed-in trailers (e.g. cross-tenant
 * migration flow) so we never silently skip the gate.
 */
export async function assertTrailerAssignable(
  trailerHumanId: string,
  tenantId: string | null,
  queryClient: QueryClient,
): Promise<AssignabilityResult> {
  const trimmed = trailerHumanId.trim();
  if (!trimmed) return { ok: true };

  const cached = queryClient.getQueryData<CachedTrailer[]>([
    "checked-in-trailers",
    tenantId,
  ]);
  const cachedMatch = cached?.find(
    (t) => t.trailer_id?.toLowerCase() === trimmed.toLowerCase(),
  );

  if (cachedMatch) {
    if (cachedMatch.needs_service) {
      return {
        ok: false,
        reason: "needs_service",
        trailerId: cachedMatch.trailer_id,
        serviceReason: cachedMatch.needs_service_reason ?? null,
      };
    }
    return { ok: true };
  }

  if (!tenantId) return { ok: true };

  const { data } = await supabase
    .from("trailers")
    .select("trailer_id, needs_service, needs_service_reason")
    .eq("trailer_id", trimmed)
    .eq("tenant_id", tenantId)
    .maybeSingle();

  if (data && (data as any).needs_service) {
    return {
      ok: false,
      reason: "needs_service",
      trailerId: (data as any).trailer_id,
      serviceReason: (data as any).needs_service_reason ?? null,
    };
  }
  return { ok: true };
}